'use es6';

import I18n from 'I18n';
import { Map as ImmutableMap } from 'immutable';
import PortalIdParser from 'PortalIdParser';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import * as ErrorTypes from 'reporting-data/constants/errorTypes';
import { Link } from 'foundations-components/transitional/components/Link';
import { Button } from 'foundations-components/transitional/components/Button';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import NoData from './NoData';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { View } from 'foundations-components/components/View';
import { SplashMessage } from '../uiWrapper/SplashMessage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const knowledgeBaseUrls = {
  [ErrorTypes.TOO_MANY_POINTS_REQUESTED]: `https://knowledge.hubspot.com/reports/understand-the-custom-report-builder#too-many-data-points-requested-error-troubleshooting`
};
const translate = (keys, options) => I18n.text(`error-messages.${keys}`, options);
const messages = {
  [ErrorTypes.ACCESS_DENIED]: () => ({
    header: translate('access-denied.header'),
    tip: translate('access-denied.message')
  }),
  [ErrorTypes.UNAUTHORIZED_ENGAGEMENTS_REPORT_READ]: () => ({
    header: translate('access-denied-engagements.header'),
    tip: translate('access-denied-engagements.message')
  }),
  [ErrorTypes.TOO_MANY_POINTS_REQUESTED]: () => ({
    header: translate('too-many-data-points.header'),
    tip: translate('too-many-data-points.message')
  }),
  [ErrorTypes.TOO_MANY_BREAKDOWNS]: () => ({
    header: translate('too-many-breakdowns.header'),
    tip: translate('too-many-breakdowns.message')
  }),
  [ErrorTypes.INVALID_PROPERTIES]: ({
    meta: {
      properties = []
    }
  }) => ({
    header: translate('invalid-properties.header'),
    tip: translate('invalid-properties.message', {
      properties: I18n.formatList(properties)
    })
  }),
  [ErrorTypes.DEPRECATED_PROPERTY]: ({
    meta: {
      property
    }
  }) => ({
    header: translate('deprecated-property.header'),
    tip: translate(`deprecated-property.message.${property}`)
  }),
  [ErrorTypes.FUNNEL_COUNT_PROPERTY_NOT_PRESENT]: ({
    meta: {
      metricName
    }
  }) => ({
    header: translate('funnel-count-not-present.header'),
    tip: translate(`funnel-count-not-present.${metricName}`)
  }),
  [ErrorTypes.FIELD_LEVEL_PERMISSIONS]: ({
    meta: {
      propertyNames
    }
  }) => ({
    header: translate('field-level-permissions.header'),
    tip: translate('field-level-permissions.message'),
    list: propertyNames
  }),
  [ErrorTypes.UNSUPPORTED]: () => ({
    header: translate('unsupported.header'),
    tip: translate('unsupported.message')
  }),
  [ErrorTypes.TIMEOUT]: () => ({
    header: translate('timeout.header'),
    tip: translate('timeout.message')
  }),
  [ErrorTypes.TOO_MANY_METRICS_REQUESTED]: () => ({
    tip: translate('too-many-metrics.message'),
    buttonText: translate('too-many-metrics.buttonText')
  }),
  [ErrorTypes.TOO_MANY_OBJECT_STAGES_REQUESTED]: () => ({
    tip: translate('too-many-stages.message'),
    buttonText: translate('too-many-stages.buttonText')
  }),
  [ErrorTypes.UNSUPPORTED_DATE_RANGE_REQUESTED]: () => ({
    header: translate('unsupported-frequency-display.header'),
    tip: translate('unsupported-frequency-display.message')
  }),
  [ErrorTypes.INVALID_PIPELINE]: () => ({
    tip: translate('invalid-pipeline.message'),
    buttonText: translate('invalid-pipeline.buttonText')
  }),
  [ErrorTypes.MISSING_REQUIRED_DATA]: () => ({
    tip: /*#__PURE__*/_jsx(NoData, {})
  }),
  [ErrorTypes.NOT_CUSTOM_OBJECT_SUPPORTED]: () => ({
    header: translate('notCustomObjectSupported')
  }),
  [ErrorTypes.REQUEST_DATASET_TOO_LARGE]: () => ({
    header: translate('too-large-dataset.header'),
    tip: translate('too-large-dataset.message')
  }),
  [ErrorTypes.PIPELINE_CADENCE_MISMATCH]: () => ({
    header: translate('pipeline-cadence-mismatch.header'),
    tip: translate('pipeline-cadence-mismatch.message')
  }),
  [ErrorTypes.UNKNOWN]: () => ({
    header: translate('generic.header'),
    tip: translate('generic.message')
  }),
  [ErrorTypes.MISSING_DIMENSIONS_AND_METRICS]: () => ({
    header: translate('no-dimensions-or-metrics.header'),
    tip: translate('no-dimensions-or-metrics.message')
  }),
  [ErrorTypes.DATA_REPROCESSING]: () => ({
    header: translate('data-reprocessing.header'),
    tip: translate('data-reprocessing.message')
  }),
  [ErrorTypes.HIGHCHARTS_ERROR]: () => ({
    header: translate('generic.header'),
    tip: translate('generic.message')
  }),
  [ErrorTypes.MISSING_INTEGRATION_ERROR]: () => ({
    header: translate('missing-integration.header'),
    tip: translate('missing-integration.message')
  }),
  [ErrorTypes.MISSING_SERIES_ERROR]: () => ({
    header: translate('missing-series.header'),
    tip: translate('missing-series.message')
  }),
  [ErrorTypes.INVALID_TWO_DIMENSION_METRIC]: ({
    meta: {
      propertyName
    }
  }) => ({
    header: translate('invalid-two-dimension-metric.header', {
      propertyName
    }),
    tip: translate('invalid-two-dimension-metric.message'),
    buttonText: translate('invalid-two-dimension-metric.buttonText')
  }),
  [ErrorTypes.MOBILE_UNSUPPORTED]: () => ({
    header: translate('mobile-unsupported.header'),
    tip: translate('mobile-unsupported.message')
  }),
  [ErrorTypes.FUTURE_DATE_UNSUPPORTED]: () => ({
    header: translate('future-date-unsupported.header'),
    tip: translate('future-date-unsupported.message')
  }),
  [ErrorTypes.TOO_MANY_SEQUENTIAL_OPTIONAL_STAGES]: () => ({
    header: translate('too-many-sequential-optional-stages.header'),
    tip: translate('too-many-sequential-optional-stages.message')
  }),
  [ErrorTypes.UNFILTERED_PIPELINE_STAGE]: () => ({
    header: translate('unfiltered-pipeline-stage.header'),
    tip: translate('unfiltered-pipeline-stage.message')
  }),
  [ErrorTypes.UNSUPPORTED_DATE_RANGE_FOR_ATTRIBUTION_REPORTS]: () => ({
    header: translate('unsupported-date-range-attribution-reports.header'),
    tip: translate('unsupported-date-range-attribution-reports.message')
  }),
  [ErrorTypes.MISSING_UNIFIED_ANALYTICS_FILTER]: () => ({
    header: translate('missing-unified-analytics-filter.header'),
    tip: translate('missing-unified-analytics-filter.message')
  })
};
const logError = (type, error) => {
  if (type === ErrorTypes.UNKNOWN) {
    console.error(`${type} ERROR in HubSpot/reporting:`, error);
  }
};
export default class ErrorMessage extends PureComponent {
  componentDidMount() {
    const {
      type,
      error
    } = this.props;
    logError(type, error);
  }
  componentDidUpdate({
    type,
    error
  }) {
    if (type !== this.props.type || this.props.error !== error) {
      logError(type, error);
    }
  }
  render() {
    const {
      type = ErrorTypes.UNKNOWN,
      error,
      editableReport,
      'data-test-id': dataTestId = 'reporting-error-message',
      isMobile = false
    } = this.props;
    const {
      header,
      tip,
      buttonText,
      list = []
    } = messages[type](error);
    const reportId = editableReport && editableReport.get('id');
    return /*#__PURE__*/_jsxs(SplashMessage, {
      "data-test-id": dataTestId,
      paddingBlock: 0,
      paddingInline: isMobile ? 0 : 250,
      textAlign: "center",
      children: [/*#__PURE__*/_jsx("h4", {
        children: header
      }), (list === null || list === void 0 ? void 0 : list.length) > 0 && /*#__PURE__*/_jsx(TFlex, {
        direction: "column",
        align: "center",
        children: /*#__PURE__*/_jsx(List, {
          paddingInlineStart: 0,
          styled: true,
          marginBlockEnd: 400,
          textAlign: "justify",
          children: list.map(item => {
            return /*#__PURE__*/_jsx(ListItem, {
              children: item
            }, item);
          })
        })
      }), /*#__PURE__*/_jsx("p", {
        children: tip
      }), knowledgeBaseUrls[type] && /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "error-messages.learn-more_jsx",
        elements: {
          Link
        },
        options: {
          external: true,
          href: knowledgeBaseUrls[type],
          target: 'blank'
        }
      }), buttonText && reportId && /*#__PURE__*/_jsx(View, {
        marginBlockStart: "200",
        as: "div",
        children: /*#__PURE__*/_jsx(Button, {
          use: "tertiary",
          href: `/report-builder/${PortalIdParser.get()}/report/${reportId}`,
          children: buttonText
        })
      })]
    });
  }
}
ErrorMessage.defaultProps = {
  type: ErrorTypes.UNKNOWN
};
ErrorMessage.propTypes = {
  type: PropTypes.oneOf(Object.keys(ErrorTypes)),
  error: PropTypes.any,
  editableReport: PropTypes.instanceOf(ImmutableMap),
  ['data-test-id']: PropTypes.string,
  isMobile: PropTypes.bool
};